(() => {
  'use strict';
  if (customElements.get('whv-shipping-estimate')) return;

  const storageKey = 'whv-shipping-country-v1';
  let manualCountry;
  let detectedCountry;
  let detection;
  let pickerId = 0;
  let countryNames;

  const countryCode = (value) => typeof value === 'string' && /^[A-Z]{2}$/.test(value.toUpperCase()) ? value.toUpperCase() : null;

  function codeFromCountryName(name) {
    if (!countryNames) {
      // Policy editors can strip data-* attributes; use browser country names, not duplicated shipping rates.
      countryNames = new Map([['czech republic', 'CZ'], ['turkey', 'TR'], ['macau', 'MO']]);
      if (typeof Intl.DisplayNames === 'function') {
        for (const style of ['long', 'short']) {
          const names = new Intl.DisplayNames(['en'], { type: 'region', style, fallback: 'none' });
          for (let first = 65; first <= 90; first++) {
            for (let second = 65; second <= 90; second++) {
              const code = String.fromCharCode(first, second);
              if (new Intl.Locale(`und-${code}`).region !== code) continue;
              const label = names.of(code);
              if (label) countryNames.set(label.toLowerCase(), code);
            }
          }
        }
      }
    }
    return countryNames.get(name.trim().toLowerCase());
  }

  function readSelection() {
    if (manualCountry) return manualCountry;
    try { return countryCode(localStorage.getItem(storageKey)); } catch { return null; }
  }

  function parsePolicy(html) {
    if (typeof html !== 'string') throw new Error('Missing shipping table.');
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const table = doc.querySelector('#whv-standard-shipping, table[data-whv-shipping-table="standard"]');
    if (!table) throw new Error('Missing standard shipping table.');
    const rows = [...table.rows];
    const headers = [...(rows.shift()?.cells || [])].map((cell) => cell.textContent.trim().toLowerCase());
    const indexes = [['country / region', 'countries'], ['transit time', 'estimate shipping time']].map((names) => headers.findIndex((header) => names.includes(header)));
    if (indexes.includes(-1)) throw new Error('Unexpected shipping table columns.');
    const countries = new Map();
    for (const row of rows) {
      const [name, time] = indexes.map((index) => row.cells[index]?.textContent.trim() || '');
      const code = codeFromCountryName(name);
      if (!code || !name || !/^\d{1,2}(?:\s*[-\u2013]\s*\d{1,2})?\s+business days?$/i.test(time)) continue;
      if (countries.has(code) && countries.get(code).time !== time) throw new Error('Conflicting shipping times.');
      countries.set(code, { name, time });
    }
    if (!countries.has('US')) throw new Error('Missing US shipping fallback.');
    return countries;
  }

  function detectCountry(url) {
    if (!detection) {
      detection = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 2500);
        try {
          const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) return null;
          const result = await response.json();
          // A storefront's selected market is not necessarily the visitor's IP country.
          detectedCountry = countryCode(result.detected_values?.country?.handle);
          return detectedCountry;
        } catch { return null; }
        finally { clearTimeout(timeout); }
      })();
    }
    return detection;
  }

  class ShippingEstimate extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.select = this.querySelector('[data-whv-country]');
      this.output = this.querySelector('[data-whv-estimate]');
      try {
        this.countries = parsePolicy(JSON.parse(this.querySelector('[data-whv-policy]').textContent));
        this.select.replaceChildren(...[...this.countries].sort((a, b) => a[1].name.localeCompare(b[1].name, 'en')).map(([code, data]) => new Option(data.name, code)));
        this.select.disabled = false;
        this.setupPicker();
        this.render(readSelection() || detectedCountry || 'US');
        this.select.addEventListener('change', () => {
          manualCountry = this.select.value;
          try { localStorage.setItem(storageKey, manualCountry); } catch { /* Private browsing may deny storage. */ }
          document.dispatchEvent(new CustomEvent('whv:shipping-country', { detail: manualCountry }));
        }, { signal: this.controller.signal });
        document.addEventListener('whv:shipping-country', (event) => this.render(event.detail), { signal: this.controller.signal });
        if (!readSelection()) {
          detectCountry(this.dataset.countryUrl).then((code) => {
            if (this.isConnected && !readSelection()) this.render(code || 'US');
          });
        }
      } catch {
        this.select.disabled = true;
        this.output.replaceChildren(document.createTextNode('Please see our '));
        const link = document.createElement('a');
        link.href = `${window.Shopify?.routes?.root || '/'}policies/shipping-policy`;
        link.textContent = 'shipping policy';
        link.className = 'link';
        this.output.append(link, document.createTextNode(' for delivery information.'));
        this.dataset.state = 'unavailable';
      }
    }

    disconnectedCallback() {
      this.closePicker();
      this.controller?.abort();
      this.controller = null;
    }

    setupPicker() {
      const wrapper = this.select.closest('.whv-shipping-estimate__select');
      if (!wrapper) return;
      wrapper.querySelectorAll('[data-whv-picker]').forEach((element) => element.remove());
      const id = `${this.select.id || `WhvShipping-${++pickerId}`}-options`;
      this.trigger = document.createElement('button');
      this.trigger.type = 'button';
      this.trigger.className = 'whv-shipping-estimate__trigger';
      this.trigger.dataset.whvPicker = '';
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-label', 'Ship to');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', id);
      this.list = document.createElement('div');
      this.list.id = id;
      this.list.className = 'whv-shipping-estimate__options';
      this.list.dataset.whvPicker = '';
      this.list.setAttribute('role', 'listbox');
      this.list.setAttribute('aria-label', 'Shipping country');
      this.list.hidden = true;
      this.items = [...this.select.options].map((option) => {
        const item = document.createElement('div');
        item.id = `${id}-${option.value}`;
        item.className = 'whv-shipping-estimate__option';
        item.setAttribute('role', 'option');
        item.setAttribute('aria-selected', 'false');
        item.dataset.value = option.value;
        item.textContent = option.textContent;
        return item;
      });
      this.list.replaceChildren(...this.items);
      wrapper.append(this.trigger, this.list);
      this.select.hidden = true;
      this.select.setAttribute('aria-hidden', 'true');
      this.select.tabIndex = -1;
      const listen = (target, type, handler) => target.addEventListener(type, handler, { signal: this.controller.signal });
      listen(this.trigger, 'click', () => this.list.hidden ? this.openPicker() : this.closePicker());
      listen(this.list, 'mousedown', (event) => event.preventDefault());
      listen(this.list, 'click', (event) => {
        const item = event.target.closest('[role="option"]');
        if (item && this.list.contains(item)) this.choose(item.dataset.value);
      });
      listen(this.trigger, 'keydown', (event) => this.pickerKeydown(event));
      listen(document, 'pointerdown', (event) => { if (!wrapper.contains(event.target)) this.closePicker(); });
      listen(wrapper, 'focusout', (event) => { if (!wrapper.contains(event.relatedTarget)) this.closePicker(); });
      listen(window, 'resize', () => this.closePicker());
      const label = this.querySelector(`label[for="${this.select.id}"]`);
      if (label) listen(label, 'click', (event) => { event.preventDefault(); this.trigger.focus(); });
    }

    openPicker() {
      if (!this.list || this.dataset.state !== 'ready') return;
      const rect = this.trigger.getBoundingClientRect();
      const below = innerHeight - rect.bottom - 13;
      const above = rect.top - 13;
      const upwards = below < 140 && above > below;
      this.list.dataset.placement = upwards ? 'top' : 'bottom';
      this.list.style.maxHeight = `${Math.max(40, Math.min(250, upwards ? above : below))}px`;
      this.list.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.activate(this.items.findIndex((item) => item.dataset.value === this.select.value));
    }

    closePicker() {
      if (!this.list) return;
      this.list.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.typeahead = '';
    }

    activate(index) {
      this.activeIndex = Math.max(0, Math.min(this.items.length - 1, index));
      this.items.forEach((item, i) => item.classList.toggle('is-active', i === this.activeIndex));
      const item = this.items[this.activeIndex];
      this.trigger.setAttribute('aria-activedescendant', item.id);
      // Scroll only the option list, never the surrounding product page.
      if (item.offsetTop < this.list.scrollTop) this.list.scrollTop = item.offsetTop;
      else if (item.offsetTop + item.offsetHeight > this.list.scrollTop + this.list.clientHeight) {
        this.list.scrollTop = item.offsetTop + item.offsetHeight - this.list.clientHeight;
      }
    }

    choose(code) {
      this.select.value = code;
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.closePicker();
      this.trigger.focus({ preventScroll: true });
    }

    pickerKeydown(event) {
      const { key } = event;
      if (key === 'Tab') { this.closePicker(); return; }
      if (key === 'Escape') {
        if (!this.list.hidden) { event.preventDefault(); event.stopPropagation(); this.closePicker(); }
        return;
      }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'Enter', ' '].includes(key)) {
        event.preventDefault();
        if (this.list.hidden) { this.openPicker(); return; }
        if (key === 'Enter' || key === ' ') { this.choose(this.items[this.activeIndex].dataset.value); return; }
        this.activate(key === 'Home' ? 0 : key === 'End' ? this.items.length - 1 : this.activeIndex + (key === 'ArrowDown' ? 1 : -1));
      } else if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        if (this.list.hidden) this.openPicker();
        this.typeahead = Date.now() - (this.typedAt || 0) > 700 ? key : (this.typeahead || '') + key;
        this.typedAt = Date.now();
        const index = this.items.findIndex((item) => item.textContent.toLowerCase().startsWith(this.typeahead.toLowerCase()));
        if (index >= 0) this.activate(index);
      }
    }

    render(requested) {
      const code = this.countries.has(requested) ? requested : 'US';
      this.select.value = code;
      if (this.trigger) {
        this.trigger.textContent = this.countries.get(code).name;
        this.items.forEach((item) => item.setAttribute('aria-selected', String(item.dataset.value === code)));
      }
      const duration = document.createElement('strong');
      duration.className = 'whv-shipping-estimate__duration';
      duration.textContent = this.countries.get(code).time;
      this.output.replaceChildren(document.createTextNode('The estimated delivery time is '), duration, document.createTextNode('.'));
      this.dataset.state = 'ready';
    }
  }

  customElements.define('whv-shipping-estimate', ShippingEstimate);
})();
